import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, catchError, delay, map, shareReplay, startWith, switchMap } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';
import { BYPASS_MOCK } from '../../../core/constants/http-context-tokens';
import { BirthdayEmployee, OrgNode, WidgetData, loadingData, errorData } from './dashboard-widget.model';
import { cleanPlaceholderValue, preferredDisplayName } from '../../../shared/utils/employee-name.util';

/** Minimal employee shape we need to build a reporting tree. */
interface RawEmployee {
  id?: string | number;
  employee_id?: string;
  employee_number?: string;
  full_name?: string;
  name?: string;
  name_with_initials?: string;
  designation?: string;
  status?: string;
  is_active?: boolean;
  core_attributes?: Record<string, any>;
  dynamic_attributes?: Record<string, any>;
}

interface Person {
  id: string;
  number?: string;
  name: string;
  title?: string;
  avatar?: string;
  /** Raw supervisor reference as stored (id, number, or name). */
  supervisorRef?: string;
}

@Injectable({ providedIn: 'root' })
export class DashboardDataRegistry {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  private readonly EMPLOYEE_API = `${(environment as any).baseUrl ?? environment.apiUrl}/employees`;

  /** The whole mock file, fetched once and shared across every widget. */
  private mock$ = this.http
    .get<Record<string, Partial<WidgetData>>>('assets/mock-data/dashboard-analytics.json')
    .pipe(shareReplay(1));

  /** Stream for a source id. Emits loading first, then the value (or '—'). */
  resolve(dataSourceId: string): Observable<WidgetData> {
    if (dataSourceId === 'org.reporting') return this.reportingHierarchy();
    if (dataSourceId === 'employee.birthdays') return this.upcomingBirthdays();
    return this.fromMock(dataSourceId);
  }

  // ── Live source: upcoming birthdays from the employee directory ────────────

  /** How many upcoming birthdays to show, soonest first (no date cutoff). */
  private readonly BIRTHDAY_LIMIT = 5;

  private upcomingBirthdays(): Observable<WidgetData> {
    const headers = new HttpHeaders({ Accept: 'application/json' });
    return this.http
      .get<RawEmployee[] | { data: RawEmployee[] }>(this.EMPLOYEE_API, {
        headers,
        params: new HttpParams().set('page_size', '1000').set('limit', '1000'),
        withCredentials: true,
        context: new HttpContext().set(BYPASS_MOCK, true),
      })
      .pipe(
        map((res) => (Array.isArray(res) ? res : (res?.data ?? []))),
        map((people) => this.toBirthdays(people)),
        map((birthdays) => ({ value: birthdays.length, birthdays, loading: false }) as WidgetData),
        startWith(loadingData()),
        catchError((err) => {
          console.error('[birthdays] employee fetch failed → using sample data', err);
          return this.fromMock('employee.birthdays');
        }),
      );
  }

  /** Map raw employees → the next N days of birthdays, soonest first. */
  private toBirthdays(people: RawEmployee[]): BirthdayEmployee[] {
    const MS_DAY = 86400000;
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const rows = people
      .map((e) => {
        const core = e.core_attributes ?? {};
        const dyn = e.dynamic_attributes ?? {};
        const raw = (core as any).birthday ?? (dyn as any).birthday ?? (e as any).birthday ?? (core as any).date_of_birth;
        if (!raw) return null;
        const dob = new Date(String(raw));
        if (isNaN(dob.getTime())) return null;

        // This year's occurrence; roll to next year if already passed.
        let next = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
        if (next.getTime() < today.getTime()) next = new Date(today.getFullYear() + 1, dob.getMonth(), dob.getDate());
        const daysAway = Math.round((next.getTime() - today.getTime()) / MS_DAY);

        const person = this.toPerson(e);
        const entry: BirthdayEmployee & { _days: number } = {
          id: person.id || String(raw),
          name: person.name,
          designation: person.title ?? '',
          avatar: person.avatar,
          birthday: daysAway === 0 ? 'Today' : `${dob.getDate()} ${MONTHS[dob.getMonth()]}`,
          _days: daysAway,
        };
        return entry;
      })
      .filter((x): x is BirthdayEmployee & { _days: number } => x !== null)
      .sort((a, b) => a._days - b._days)
      .slice(0, this.BIRTHDAY_LIMIT);

    return rows.map(({ _days, ...b }) => b);
  }

  // ── Mock-backed sources ───────────────────────────────────────────────────

  private fromMock(dataSourceId: string): Observable<WidgetData> {
    return this.mock$.pipe(
      // A touch of latency so the loading skeletons are visible in the demo.
      delay(350),
      map((all) => {
        const entry = all?.[dataSourceId];
        if (!entry) {
          console.warn('Unknown dashboard data source:', dataSourceId);
          return errorData();
        }
        return { ...entry, loading: false } as WidgetData;
      }),
      startWith(loadingData()),
      catchError(() => of(errorData())),
    );
  }

  /** The mock reporting tree — used as the fallback for org.reporting. */
  private mockReporting(): Observable<WidgetData> {
    return this.mock$.pipe(
      map((all) => ({ ...(all?.['org.reporting'] ?? {}), loading: false }) as WidgetData),
      catchError(() => of(errorData())),
    );
  }

  // ── Live source: the logged-in user's reporting hierarchy ─────────────────

  private reportingHierarchy(): Observable<WidgetData> {
    const me = this.auth.loggedEmployeeId();
    if (!me) return this.mockReporting();

    const headers = new HttpHeaders({ Accept: 'application/json' });
    return this.http
      .get<RawEmployee[] | { data: RawEmployee[] }>(this.EMPLOYEE_API, {
        headers,
        params: new HttpParams().set('page_size', '1000').set('limit', '1000'),
        withCredentials: true,
        context: new HttpContext().set(BYPASS_MOCK, true),
      })
      .pipe(
        map((res) => (Array.isArray(res) ? res : (res?.data ?? [])).map((e) => this.toPerson(e))),
        map((people) => {
          // DEBUG — remove once the reporting line is verified against live data.
          const self = people.find((p) => this.matches(p, me));
          console.log('[reporting] logged employee id:', me);
          console.log('[reporting] employees loaded:', people.length, people);
          console.log('[reporting] matched self:', self);
          if (self) {
            console.log('[reporting] my hod/sub_hod ref:', self.supervisorRef);
            console.log(
              '[reporting] my reports:',
              people.filter((p) => p.id !== self.id && p.supervisorRef && this.matches(self, p.supervisorRef)),
            );
          }
          return this.buildReportingTree(people, me);
        }),
        // Placed the user → build the widget; couldn't → fall back to the mock tree.
        switchMap((org) => {
          if (!org) console.warn('[reporting] user not resolved in employee list → using sample tree');
          return org
            ? of({ value: 'reporting line', org, loading: false } as WidgetData)
            : this.mockReporting();
        }),
        startWith(loadingData()),
        catchError((err) => {
          console.error('[reporting] employee fetch failed → using sample tree', err);
          return this.mockReporting();
        }),
      );
  }

  private toPerson(e: RawEmployee): Person {
    const core = e.core_attributes ?? {};
    const dyn = e.dynamic_attributes ?? {};
    const pick = (...keys: string[]): string | undefined => {
      for (const k of keys) {
        const v = (core as any)[k] ?? (dyn as any)[k] ?? (e as any)[k];
        const cleaned = cleanPlaceholderValue(v == null ? '' : String(v));
        if (cleaned) return cleaned;
      }
      return undefined;
    };
    const id = String(e.employee_id ?? e.id ?? '');

    const name =
      preferredDisplayName(e) ||
      pick('name') ||
      pick('employee_number', 'employee_no', 'emp_no') ||
      '';

    const title = pick('designation_name', 'job_title');

    return {
      id,
      number: pick('employee_number', 'employee_no', 'emp_no'),
      name: name || `Employee ${id}`,
      title,
      avatar: pick('profile_picture', 'avatar', 'photo', 'image_url'),
      // Direct reporting line = Primary Supervisor; HOD / sub-HOD are fallbacks.
      // All store an employee id.
      supervisorRef: pick('supervisor', 'hod', 'sub_hod', 'reporting_line_person'),
    };
  }

  /**
   * Build manager → you → reports. Returns null if the logged-in user isn't in
   * the list (caller then falls back to the mock tree).
   */
  private buildReportingTree(people: Person[], myId: string): OrgNode | null {
    const self = people.find((p) => this.matches(p, myId));
    if (!self) return null;

    // A supervisor ref can be an id, an employee number, or a name — match any.
    const resolve = (ref?: string): Person | undefined =>
      ref ? people.find((p) => this.matches(p, ref)) : undefined;

    const reports = people.filter(
      (p) => p.id !== self.id && p.supervisorRef && this.matches(self, p.supervisorRef),
    );
    const manager = resolve(self.supervisorRef);

    const selfNode: OrgNode = {
      name: self.name,
      title: self.title,
      avatar: self.avatar,
      self: true,
      children: reports.map((r) => ({ name: r.name, title: r.title, avatar: r.avatar })),
    };

    return manager
      ? { name: manager.name, title: manager.title, avatar: manager.avatar, children: [selfNode] }
      : selfNode;
  }

  /** True if `p` is identified by `ref` (its id, employee number, or name). */
  private matches(p: Person, ref: string): boolean {
    const r = ref.trim().toLowerCase();
    return (
      p.id.toLowerCase() === r ||
      (p.number?.toLowerCase() === r) ||
      p.name.toLowerCase() === r
    );
  }
}
